var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// src/constants/numbers.ts
import JSBI3 from "jsbi";

// src/functions/percent.ts
import numeral from "numeral";
var formatPercent = (value) => {
  if (value < 1e-3) {
    return "0.00%";
  }
  return numeral(value).format("0.00%");
};

// src/entities/Percent.ts
import JSBI2 from "jsbi";

// src/entities/Fraction.ts
import JSBI from "jsbi";
import invariant from "tiny-invariant";

// src/utils/Big.ts
import Big from "big.js";
import toFormat from "toformat";
Big.strict = true;
var Big_default = toFormat(Big);

// src/utils/Decimal.ts
import Decimal from "decimal.js-light";
import toFormat2 from "toformat";
var Decimal_default = toFormat2(Decimal);

// src/utils/index.ts
import { default as default2 } from "jsbi";

// src/enums/Rounding.ts
var Rounding = /* @__PURE__ */ ((Rounding2) => {
  Rounding2[Rounding2["ROUND_DOWN"] = 0] = "ROUND_DOWN";
  Rounding2[Rounding2["ROUND_HALF_UP"] = 1] = "ROUND_HALF_UP";
  Rounding2[Rounding2["ROUND_UP"] = 3] = "ROUND_UP";
  return Rounding2;
})(Rounding || {});
var Rounding_default = Rounding;

// src/entities/Fraction.ts
var toSignificantRounding = {
  [Rounding_default.ROUND_DOWN]: Decimal_default.ROUND_DOWN,
  [Rounding_default.ROUND_HALF_UP]: Decimal_default.ROUND_HALF_UP,
  [Rounding_default.ROUND_UP]: Decimal_default.ROUND_UP
};
var toFixedRounding = {
  [Rounding_default.ROUND_DOWN]: Big_default.roundDown,
  [Rounding_default.ROUND_HALF_UP]: Big_default.roundHalfUp,
  [Rounding_default.ROUND_UP]: Big_default.roundUp
};
var Fraction = class {
  /**
   * @param {BigintIsh}numerator
   * @param {BigintIsh}denominator
   */
  constructor(numerator, denominator = JSBI.BigInt(1)) {
    this.numerator = JSBI.BigInt(numerator);
    this.denominator = JSBI.BigInt(denominator);
  }
  /**
   * @param {BigintIsh | Fraction}fractionish
   * @return {Fraction}
   */
  static tryParseFraction(fractionish) {
    if (fractionish instanceof JSBI || typeof fractionish === "number" || typeof fractionish === "string")
      return new Fraction(fractionish);
    if ("numerator" in fractionish && "denominator" in fractionish)
      return fractionish;
    throw new Error("Could not parse fraction");
  }
  /** performs floor division */
  get quotient() {
    return JSBI.divide(this.numerator, this.denominator);
  }
  /** remainder after floor division*/
  get remainder() {
    return new Fraction(JSBI.remainder(this.numerator, this.denominator), this.denominator);
  }
  /**
   * @return{Fraction}
   */
  invert() {
    return new Fraction(this.denominator, this.numerator);
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Fraction}
   */
  add(other) {
    const otherParsed = Fraction.tryParseFraction(other);
    if (JSBI.equal(this.denominator, otherParsed.denominator)) {
      return new Fraction(JSBI.add(this.numerator, otherParsed.numerator), this.denominator);
    }
    return new Fraction(
      JSBI.add(
        JSBI.multiply(this.numerator, otherParsed.denominator),
        JSBI.multiply(otherParsed.numerator, this.denominator)
      ),
      JSBI.multiply(this.denominator, otherParsed.denominator)
    );
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Fraction}
   */
  subtract(other) {
    const otherParsed = Fraction.tryParseFraction(other);
    if (JSBI.equal(this.denominator, otherParsed.denominator)) {
      return new Fraction(JSBI.subtract(this.numerator, otherParsed.numerator), this.denominator);
    }
    return new Fraction(
      JSBI.subtract(
        JSBI.multiply(this.numerator, otherParsed.denominator),
        JSBI.multiply(otherParsed.numerator, this.denominator)
      ),
      JSBI.multiply(this.denominator, otherParsed.denominator)
    );
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {boolean}
   */
  lessThan(other) {
    const otherParsed = Fraction.tryParseFraction(other);
    return JSBI.lessThan(
      JSBI.multiply(this.numerator, otherParsed.denominator),
      JSBI.multiply(otherParsed.numerator, this.denominator)
    );
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {boolean}
   */
  equalTo(other) {
    const otherParsed = Fraction.tryParseFraction(other);
    return JSBI.equal(
      JSBI.multiply(this.numerator, otherParsed.denominator),
      JSBI.multiply(otherParsed.numerator, this.denominator)
    );
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {boolean}
   */
  greaterThan(other) {
    const otherParsed = Fraction.tryParseFraction(other);
    return JSBI.greaterThan(
      JSBI.multiply(this.numerator, otherParsed.denominator),
      JSBI.multiply(otherParsed.numerator, this.denominator)
    );
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Fraction}
   */
  multiply(other) {
    const otherParsed = Fraction.tryParseFraction(other);
    return new Fraction(
      JSBI.multiply(this.numerator, otherParsed.numerator),
      JSBI.multiply(this.denominator, otherParsed.denominator)
    );
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Fraction}
   */
  divide(other) {
    const otherParsed = Fraction.tryParseFraction(other);
    return new Fraction(
      JSBI.multiply(this.numerator, otherParsed.denominator),
      JSBI.multiply(this.denominator, otherParsed.numerator)
    );
  }
  /**
   * @param {number}significantDigits
   * @param {object} format
   * @param {Rounding}rounding
   * @return {string}
   */
  toSignificant(significantDigits, format = { groupSeparator: "" }, rounding = Rounding_default.ROUND_HALF_UP) {
    invariant(Number.isInteger(significantDigits), `${significantDigits} is not an integer.`);
    invariant(significantDigits > 0, `${significantDigits} is not positive.`);
    Decimal_default.set({
      precision: significantDigits + 1,
      rounding: toSignificantRounding[rounding]
    });
    const quotient = new Decimal_default(this.numerator.toString()).div(this.denominator.toString()).toSignificantDigits(significantDigits);
    return quotient.toFormat(quotient.decimalPlaces(), format);
  }
  /**
   * @param {number}decimalPlaces
   * @param {object} format
   * @param {Rounding}rounding
   * @return {string}
   */
  toFixed(decimalPlaces, format = { groupSeparator: "" }, rounding = Rounding_default.ROUND_HALF_UP) {
    invariant(Number.isInteger(decimalPlaces), `${decimalPlaces} is not an integer.`);
    invariant(decimalPlaces >= 0, `${decimalPlaces} is negative.`);
    Big_default.DP = decimalPlaces;
    Big_default.RM = toFixedRounding[rounding];
    return new Big_default(this.numerator.toString()).div(this.denominator.toString()).toFormat(decimalPlaces, format);
  }
  /**
   * Helper method for converting any super class back to a fraction
   */
  get asFraction() {
    return new Fraction(this.numerator, this.denominator);
  }
};
var Fraction_default = Fraction;

// src/entities/Percent.ts
var ONE_HUNDRED = new Fraction_default(JSBI2.BigInt(100));
function toPercent(fraction) {
  return new Percent(fraction.numerator, fraction.denominator);
}
var Percent = class extends Fraction_default {
  constructor() {
    super(...arguments);
    /**
     * This boolean prevents a fraction from being interpreted as a Percent
     */
    this.isPercent = true;
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Percent}
   */
  add(other) {
    return toPercent(super.add(other));
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Percent}
   */
  subtract(other) {
    return toPercent(super.subtract(other));
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Percent}
   */
  multiply(other) {
    return toPercent(super.multiply(other));
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Percent}
   */
  divide(other) {
    return toPercent(super.divide(other));
  }
  /**
   * @param {number}significantDigits
   * @param {object}format
   * @param {Rounding}rounding
   * @return {string}
   */
  toSignificant(significantDigits = 5, format, rounding) {
    return super.multiply(ONE_HUNDRED).toSignificant(significantDigits, format, rounding);
  }
  /**
   * @param {number}decimalPlaces
   * @param {object}format
   * @param {Rounding}rounding
   * @return {string}
   */
  toFixed(decimalPlaces = 2, format, rounding) {
    return super.multiply(ONE_HUNDRED).toFixed(decimalPlaces, format, rounding);
  }
  /**
   * @param {number} decimalPlaces
   * @return {string}
   */
  toPercentageString(decimalPlaces = 2) {
    return formatPercent(super.toFixed(decimalPlaces + 2));
  }
};
var Percent_default = Percent;

// src/constants/numbers.ts
var ZERO = JSBI3.BigInt(0);
var ONE = JSBI3.BigInt(1);
var TWO = JSBI3.BigInt(2);
var THREE = JSBI3.BigInt(3);
var FIVE = JSBI3.BigInt(5);
var TEN = JSBI3.BigInt(10);
var _100 = JSBI3.BigInt(100);
var _997 = JSBI3.BigInt(997);
var _1000 = JSBI3.BigInt(1e3);
var _9994 = JSBI3.BigInt(9994);
var _9995 = JSBI3.BigInt(9995);
var _10000 = JSBI3.BigInt(1e4);
var _1e18 = JSBI3.BigInt(1e18);
var _1e12 = JSBI3.BigInt(1e12);
var _1e9 = JSBI3.BigInt(1e9);
var _1e6 = JSBI3.BigInt(1e6);
var MAX_SAFE_INTEGER = JSBI3.BigInt(Number.MAX_SAFE_INTEGER);
var MAX_UINT256 = JSBI3.BigInt(
  "0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff"
);
var MAX_UINT128 = JSBI3.subtract(
  JSBI3.exponentiate(JSBI3.BigInt("2"), JSBI3.BigInt(128)),
  JSBI3.BigInt(1)
);
var ZERO_PERCENT = new Percent_default(0);

// src/enums/ChainId.ts
var ChainId = /* @__PURE__ */ ((ChainId2) => {
  ChainId2[ChainId2["SMARTBCH"] = 1e4] = "SMARTBCH";
  ChainId2[ChainId2["SMARTBCH_TESTNET"] = 10001] = "SMARTBCH_TESTNET";
  ChainId2[ChainId2["BSC"] = 56] = "BSC";
  ChainId2[ChainId2["BSC_TESTNET"] = 97] = "BSC_TESTNET";
  return ChainId2;
})(ChainId || {});
var ChainId_default = ChainId;

// src/functions/sqrt.ts
import invariant2 from "tiny-invariant";

// src/entities/Token.ts
import { getAddress } from "@ethersproject/address";
import invariant4 from "tiny-invariant";

// src/entities/Currency.ts
import invariant3 from "tiny-invariant";
var Currency = class {
  /**
   * Constructs an instance of the abstract class `Currency`.
   * @param {number | string}chainId the chain ID on which this currency resides
   * @param {number | string}decimals decimals of the currency
   * @param {string}symbol symbol of the currency
   * @param {string}name of the currency
   * @param {object}rebase of the currency
   */
  constructor({
    chainId,
    decimals,
    symbol,
    name,
    rebase = { base: default2.BigInt(1), elastic: default2.BigInt(1) }
  }) {
    invariant3(Number.isSafeInteger(Number(chainId)), "CHAIN_ID");
    invariant3(
      Number(decimals) >= 0 && Number(decimals) < 255 && Number.isInteger(Number(decimals)),
      "DECIMALS"
    );
    this.chainId = Number(chainId);
    this.decimals = Number(decimals);
    this.symbol = symbol;
    this.name = name;
    this.rebase = rebase;
  }
};

// src/entities/Token.ts
var Token = class extends Currency {
  /***/
  constructor({
    // TODO:
    // id,
    chainId,
    address,
    decimals,
    symbol,
    name,
    rebase = { base: default2.BigInt(1), elastic: default2.BigInt(1) }
  }) {
    super({
      chainId,
      decimals,
      symbol,
      name
    });
    this.isNative = false;
    this.isToken = true;
    try {
      this.address = getAddress(address);
      this.id = `${chainId}:${address}`;
    } catch (e) {
      console.log`${address} is not a valid address`;
    }
    try {
      this.rebase = rebase;
    } catch (e) {
      console.log`${rebase} is not a valid rebase`;
    }
  }
  /**
   * Returns true if the two tokens are equivalent, i.e. have the same chainId and address.
   * @param {Type}other other token to compare
   * @return {boolean}
   */
  equals(other) {
    return other.isToken && this.chainId === other.chainId && this.address === other.address;
  }
  /**
   * Returns true if the address of this token sorts before the address of the other token
   * @param {Token}other other token to compare
   * @throws if the tokens have the same address
   * @throws if the tokens are on different chains
   * @return {boolean}
   */
  sortsBefore(other) {
    invariant4(this.chainId === other.chainId, "CHAIN_IDS");
    invariant4(this.address !== other.address, "ADDRESSES");
    return this.address.toLowerCase() < other.address.toLowerCase();
  }
  /**
   * Return this token, which does not need to be wrapped
   */
  get wrapped() {
    return this;
  }
};

// src/functions/addressMapToTokenMap.ts
function addressMapToTokenMap({ decimals, symbol, name }, map) {
  return Object.fromEntries(
    Object.entries(map).map(([chainId, address]) => [
      chainId,
      new Token({
        chainId,
        address,
        decimals,
        symbol,
        name
      })
    ])
  );
}

// src/functions/tryParseAmount.ts
import { parseUnits } from "@ethersproject/units";

// src/entities/Amount.ts
import invariant6 from "tiny-invariant";

// src/entities/Share.ts
import invariant5 from "tiny-invariant";

// src/functions/isChainSupported.ts
import memoize from "lodash.memoize";

// src/entities/Chain.ts
var Chain = class {
  /**
   * @param {number}chainId
   * @return {object}
   */
  static from(chainId) {
    return chains[chainId];
  }
  /**
   * @param {string}shortName
   * @return {object} Shortname
   */
  static fromShortName(shortName) {
    return chains[chainShortName[shortName]];
  }
  /**
   * @param {number}chainId
   * @return {object}
   */
  static fromChainId(chainId) {
    return chains[chainId];
  }
  /**
   * @param {Chain}data
   */
  constructor(data) {
    Object.assign(this, data);
  }
  /**
   * @param {string}txHash
   * @return {string} Explorer URL
   */
  getTxUrl(txHash) {
    if (!this.blockExplorers)
      return "";
    for (const explorer of this.blockExplorers.default.url) {
      return `${explorer}/tx/${txHash}`;
    }
    return "";
  }
  /**
   * @param {string}blockHashOrHeight
   * @return {string} Explorer URL
   */
  getBlockUrl(blockHashOrHeight) {
    if (!this.blockExplorers)
      return "";
    for (const explorer of this.blockExplorers.default.url) {
      return `${explorer}/block/${blockHashOrHeight}`;
    }
    return "";
  }
  /**
   * @param {string}tokenAddress
   * @return {string} Explorer URL
   */
  getTokenUrl(tokenAddress) {
    if (!this.blockExplorers)
      return "";
    for (const explorer of this.blockExplorers.default.url) {
      return `${explorer}/token/${tokenAddress}`;
    }
    return "";
  }
  /**
   * @param {string} accountAddress
   * @return {string} Explorer URL
   */
  getAccountUrl(accountAddress) {
    if (!this.blockExplorers)
      return "";
    for (const explorer of this.blockExplorers.default.url) {
      return `${explorer}/address/${accountAddress}`;
    }
    return "";
  }
};

// src/constants/chains.json
var chains_default = [
  {
    id: 56,
    name: "Binance Smart Chain Mainnet",
    network: "BSC",
    rpcUrls: {
      default: {
        http: [
          "https://bsc-dataseed1.binance.org",
          "https://bsc-dataseed2.binance.org",
          "https://bsc-dataseed3.binance.org",
          "https://bsc-dataseed4.binance.org",
          "https://bsc-dataseed1.defibit.io",
          "https://bsc-dataseed2.defibit.io",
          "https://bsc-dataseed3.defibit.io",
          "https://bsc-dataseed4.defibit.io",
          "https://bsc-dataseed1.ninicoin.io",
          "https://bsc-dataseed2.ninicoin.io",
          "https://bsc-dataseed3.ninicoin.io",
          "https://bsc-dataseed4.ninicoin.io"
        ],
        wss: ["wss://bsc-ws-node.nariox.org"]
      },
      public: {
        http: [
          "https://bsc-dataseed1.binance.org",
          "https://bsc-dataseed2.binance.org",
          "https://bsc-dataseed3.binance.org",
          "https://bsc-dataseed4.binance.org",
          "https://bsc-dataseed1.defibit.io",
          "https://bsc-dataseed2.defibit.io",
          "https://bsc-dataseed3.defibit.io",
          "https://bsc-dataseed4.defibit.io",
          "https://bsc-dataseed1.ninicoin.io",
          "https://bsc-dataseed2.ninicoin.io",
          "https://bsc-dataseed3.ninicoin.io",
          "https://bsc-dataseed4.ninicoin.io"
        ],
        wss: ["wss://bsc-ws-node.nariox.org"]
      }
    },
    nativeCurrency: { name: "Binance Chain Native Token", symbol: "BNB", decimals: 18 },
    faucets: ["https://free-online-app.com/faucet-for-eth-evm-chains/"],
    infoURL: "https://www.binance.org",
    shortName: "BSC",
    blockExplorers: {
      default: {
        name: "bscscan",
        url: "https://bscscan.com"
      }
    }
  },
  {
    id: 97,
    name: "Binance Smart Chain Testnet",
    network: "BSC",
    rpcUrls: {
      default: {
        http: [
          "https://data-seed-prebsc-1-s1.binance.org:8545",
          "https://data-seed-prebsc-2-s1.binance.org:8545",
          "https://data-seed-prebsc-1-s2.binance.org:8545",
          "https://data-seed-prebsc-2-s2.binance.org:8545",
          "https://data-seed-prebsc-1-s3.binance.org:8545",
          "https://data-seed-prebsc-2-s3.binance.org:8545"
        ]
      },
      public: {
        http: [
          "https://data-seed-prebsc-1-s1.binance.org:8545",
          "https://data-seed-prebsc-2-s1.binance.org:8545",
          "https://data-seed-prebsc-1-s2.binance.org:8545",
          "https://data-seed-prebsc-2-s2.binance.org:8545",
          "https://data-seed-prebsc-1-s3.binance.org:8545",
          "https://data-seed-prebsc-2-s3.binance.org:8545"
        ]
      }
    },
    nativeCurrency: { name: "Binance Chain Native Token", symbol: "tBNB", decimals: 18 },
    shortName: "BSC Testnet",
    blockExplorers: {
      default: {
        name: "bscscan",
        url: "https://testnet.bscscan.com"
      }
    },
    faucets: ["https://testnet.binance.org/faucet-smart"],
    infoURL: "https://testnet.binance.org/"
  },
  {
    id: 568,
    name: "Dogechain Testnet",
    network: "DC",
    rpcUrls: {
      default: {
        http: ["https://rpc-testnet.dogechain.dog"]
      },
      public: {
        http: ["https://rpc-testnet.dogechain.dog"]
      }
    },
    nativeCurrency: { name: "Dogecoin", symbol: "DOGE", decimals: 18 },
    faucets: ["https://faucet.dogechain.dog"],
    infoURL: "https://dogechain.dog",
    shortName: "dct",
    blockExplorers: {
      default: {
        name: "Doge Explorer",
        url: "https://explorer-testnet.dogechain.dog"
      }
    }
  },
  {
    id: 2e3,
    name: "Dogechain Mainnet",
    network: "DC",
    icon: "dogechain",
    rpcUrls: {
      default: {
        http: [
          "https://rpc-sg.dogechain.dog",
          "https://rpc-us.dogechain.dog",
          "https://rpc.dogechain.dog",
          "https://rpc01-sg.dogechain.dog",
          "https://rpc02-sg.dogechain.dog",
          "https://rpc03-sg.dogechain.dog"
        ]
      },
      public: {
        http: [
          "https://rpc-sg.dogechain.dog",
          "https://rpc-us.dogechain.dog",
          "https://rpc.dogechain.dog",
          "https://rpc01-sg.dogechain.dog",
          "https://rpc02-sg.dogechain.dog",
          "https://rpc03-sg.dogechain.dog"
        ]
      }
    },
    nativeCurrency: { name: "Dogecoin", symbol: "DOGE", decimals: 18 },
    faucets: [],
    infoURL: "https://dogechain.dog",
    shortName: "dc",
    blockExplorers: {
      default: {
        name: "Doge Explorer",
        url: "https://explorer.dogechain.dog"
      }
    }
  },
  {
    id: 1e4,
    chainId: 1e4,
    name: "smartBCH",
    network: "smartBCH",
    rpcUrls: {
      default: {
        http: [
          "https://smartbch.greyh.at",
          "https://global.uat.cash",
          "https://rpc-mainnet.smartbch.org",
          "https://smartbch.fountainhead.cash/mainnet",
          "https://smartbch.devops.cash/mainnet",
          "https://rpc.uatvo.com"
        ]
      },
      public: {
        http: [
          "https://smartbch.fountainhead.cash/mainnet",
          "https://smartbch.devops.cash/mainnet",
          "https://rpc.uatvo.com",
          "https://smartbch.greyh.at",
          "https://global.uat.cash",
          "https://rpc-mainnet.smartbch.org"
        ]
      }
    },
    contracts: {
      multicall3: {
        address: "0x531fCcA0aE03f8fA31475920556d59392e9ea000",
        blockCreated: 10751405
      }
    },
    nativeCurrency: { name: "Bitcoin Cash", symbol: "BCH", decimals: 18 },
    faucets: [],
    infoURL: "https://smartbch.org/",
    shortName: "smartBCH",
    blockExplorers: {
      default: {
        name: "sonar",
        url: "https://sonar.cash"
      }
    }
  },
  {
    id: 10001,
    chainId: 10001,
    name: "smartBCH Amber",
    network: "smartBCHTest",
    rpcUrls: {
      default: {
        http: ["https://rpc-testnet.smartbch.org", "https://smartbch.devops.cash/testnet"]
      },
      public: {
        http: ["https://rpc-testnet.smartbch.org", "https://smartbch.devops.cash/testnet"]
      }
    },
    contracts: {
      multicall3: {
        address: "0x1b0Ff51BD7B3461E9A960a45318b5E063c4A22cb",
        blockCreated: 11145844
      }
    },
    nativeCurrency: { name: "Bitcoin Cash Test Token", symbol: "BCHT", decimals: 18 },
    faucets: [],
    infoURL: "http://smartbch.org/",
    shortName: "smartBCH Amber",
    blockExplorers: {
      default: {
        name: "sonar",
        url: "https://testnet.sonar.cash"
      }
    }
  }
];

// src/constants/chain.ts
var SUPPORTED_CHAIN_IDS = [
  ChainId_default.SMARTBCH,
  ChainId_default.SMARTBCH_TESTNET,
  ChainId_default.BSC,
  ChainId_default.BSC_TESTNET
];
var BLOCK_DELAY = {
  [ChainId_default.BSC]: 7500,
  [ChainId_default.BSC_TESTNET]: 7500,
  [ChainId_default.SMARTBCH]: 0,
  [ChainId_default.SMARTBCH_TESTNET]: 0
};
var CHAINS = chains_default;
var chainIds = CHAINS.map((chain) => chain.id);
var chainShortNameToChainId = Object.fromEntries(
  CHAINS.map((data) => [data.shortName, data.id])
);
var chainShortName = Object.fromEntries(
  CHAINS.map((data) => [data.id, data.shortName])
);
var chainName = Object.fromEntries(
  CHAINS.map((data) => [data.id, data.name])
);
var chains = Object.fromEntries(
  CHAINS.map((data) => [data.id, new Chain(data)])
);

// src/functions/isChainSupported.ts
var isChainSupported = memoize((chainId) => chainIds.includes(chainId));

// src/functions/computePairAddress.ts
import { getCreate2Address } from "@ethersproject/address";
import { keccak256, pack } from "@ethersproject/solidity";

// src/functions/useNativeCurrency.ts
import { useMemo } from "react";

// src/entities/Native.ts
import invariant7 from "tiny-invariant";
var _Native = class extends Currency {
  /**
   * @param {object}native
   */
  constructor(native) {
    super(native);
    this.isNative = true;
    this.isToken = false;
    this.id = `${native.chainId}:NATIVE`;
    this.symbol = native.symbol;
    this.name = native.name;
  }
  /***/
  get wrapped() {
    const wnative = WNATIVE[this.chainId];
    invariant7(!!wnative, "WRAPPED");
    return wnative;
  }
  /**
   * @param {number}chainId
   * @return {Native}
   */
  static onChain(chainId) {
    if (chainId in this.cache) {
      return this.cache[chainId];
    }
    invariant7(!!(chainId in chains), "CHAINS");
    const { nativeCurrency } = chains[chainId];
    invariant7(!!nativeCurrency, "NATIVE_CURRENCY");
    const { decimals, name, symbol } = nativeCurrency;
    return this.cache[chainId] = new _Native({
      chainId,
      decimals,
      name,
      symbol
    });
  }
  /**
   * @param {Type}other
   * @return {boolean}
   */
  equals(other) {
    return other.isNative && other.chainId === this.chainId;
  }
};
var Native = _Native;
Native.cache = {};

// src/entities/Price.ts
import invariant8 from "tiny-invariant";

// src/entities/Error.ts
var CAN_SET_PROTOTYPE = "setPrototypeOf" in Object;

// src/entities/Pair.ts
import invariant9 from "tiny-invariant";

// src/entities/Trade.ts
import invariant10 from "tiny-invariant";

// src/entities/SushiSwapRouter.ts
import { getAddress as getAddress2 } from "@ethersproject/address";
import invariant11 from "tiny-invariant";
import warning from "tiny-warning";

// src/entities/Exchange.ts
import { getAddress as getAddress3 } from "@ethersproject/address";
import invariant12 from "tiny-invariant";

// src/entities/pools/PoolCode.ts
var PoolCode = class {
  /**
   * @param {RPool}pool
   * @param {LiquidityProviders}liquidityProvider
   * @param {string}poolName
   * @param {string}exchangeUri
   */
  constructor(pool, liquidityProvider, poolName, exchangeUri) {
    this.pool = pool;
    this.liquidityProvider = liquidityProvider;
    this.poolName = poolName;
    this.exchangeUri = exchangeUri;
  }
  // the address where should be swap amount of liquidity before the swap
  // returns RouteProcessorAddress if it is a RouteProcessor
  /**
   * @param {RouteLeg}_leg
   * @param {MultiRoute}_route
   * @return {string}
   */
  getStartPoint(_leg, _route) {
    return this.pool.address;
  }
  /**
   * @param {RouteLeg}_leg
   * @param {MultiRoute}_route
   * @param {string}_to
   * @return {string}
   */
  getSwapCodeForRouteProcessor2(_leg, _route, _to) {
    return "unimplemented";
  }
};
PoolCode.RouteProcessorAddress = "RouteProcessor";

// src/entities/WrappedTokenInfo.ts
import { getAddress as getAddress4, isAddress } from "@ethersproject/address";

// src/constants/addresses.ts
var INIT_CODE_HASH = {
  [ChainId_default.SMARTBCH]: "0x4eae98fedf0a51d24c5a3a59dddb69e9fbeead94344889e370a14507c35f9b4a",
  [ChainId_default.SMARTBCH_TESTNET]: "0x4eae98fedf0a51d24c5a3a59dddb69e9fbeead94344889e370a14507c35f9b4a"
};
var ROUTER_PROCESSOR_ADDRESS = {
  [ChainId_default.SMARTBCH]: "0xc420437d9E89488F90644F8a226a50D767c51BD8",
  [ChainId_default.SMARTBCH_TESTNET]: "0xe60B5F714e73281d97738ac34e8e86593E32A32A"
};
var VAULT_ADDRESS = {
  [ChainId_default.SMARTBCH]: "0xFFbE92fDA81f853bcf00d3c7686d5DAd5A6600bB",
  [ChainId_default.SMARTBCH_TESTNET]: "0xe5aFde6eD9E2aa69B1D7247E406399CBf26d7578"
};
var LOCKER_ADDRESS = {
  [ChainId_default.SMARTBCH]: "0xd9647E506Da0865f9a4E70fBD0aDA55b2B89E5b1",
  [ChainId_default.SMARTBCH_TESTNET]: "0x0714b92E09f198e53B632EbC46c78BDABE1DAd1c"
};
var FACTORY_ADDRESS = {
  [ChainId_default.SMARTBCH]: "0xE62983a68679834eD884B9673Fb6aF13db740fF0",
  [ChainId_default.SMARTBCH_TESTNET]: "0xa27588D9cff3130AC91C39b1B1c992976d492476"
};
var ROUTER_ADDRESS = {
  [ChainId_default.SMARTBCH]: "0x217057A8B0bDEb160829c19243A2E03bfe95555a",
  [ChainId_default.SMARTBCH_TESTNET]: "0xD3f256a03bf7c75ba4d08bf2847Cc7F59168DB45"
};
var DISTRIBUTOR_ADDRESS = {
  [ChainId_default.SMARTBCH]: "0x8ecb32C33AB3f7ee3D6Ce9D4020bC53fecB36Be9",
  [ChainId_default.SMARTBCH_TESTNET]: "0xFFbE92fDA81f853bcf00d3c7686d5DAd5A6600bB"
};
var MULTICALL2_ADDRESS = {
  [ChainId_default.SMARTBCH]: "0x20F9f219743556c06D2205F92548278CEd14A38B",
  [ChainId_default.SMARTBCH_TESTNET]: "0x10334769312D07dadF40bDd2217EFCae80E77910",
  [ChainId_default.BSC]: "0xa9193376D09C7f31283C54e56D013fCF370Cd9D9",
  [ChainId_default.BSC_TESTNET]: "0xA6949B8FBa9DF546b9c66F98CFCa960A96E3b68e"
};
var EMBER_ADDRESS = {
  [ChainId_default.SMARTBCH]: "0x6BAbf5277849265b6738e75AEC43AEfdde0Ce88D",
  [ChainId_default.SMARTBCH_TESTNET]: "0x217057A8B0bDEb160829c19243A2E03bfe95555a",
  [ChainId_default.BSC]: "0x6BAbf5277849265b6738e75AEC43AEfdde0Ce88D",
  [ChainId_default.BSC_TESTNET]: "0x217057A8B0bDEb160829c19243A2E03bfe95555a"
};
var FIRE_ADDRESS = {
  [ChainId_default.SMARTBCH]: "0x225FCa2A940cd5B18DFb168cD9B7f921C63d7B6E",
  [ChainId_default.SMARTBCH_TESTNET]: "0xa10da28C8D63473fD2bE8df9C37A628A188F43ea",
  [ChainId_default.BSC]: "0x6BAbf5277849265b6738e75AEC43AEfdde0Ce88D",
  [ChainId_default.BSC_TESTNET]: "0x217057A8B0bDEb160829c19243A2E03bfe95555a"
};
var EMBER = addressMapToTokenMap(
  {
    decimals: 18,
    symbol: "EMBER",
    name: "Ember"
  },
  EMBER_ADDRESS
);
var FIRE = addressMapToTokenMap(
  {
    decimals: 18,
    symbol: "FIRE",
    name: "Incinerate"
  },
  FIRE_ADDRESS
);
var BUSD_ADDRESS = {
  [ChainId_default.BSC]: "0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56"
};
var BUSD = addressMapToTokenMap(
  {
    decimals: 18,
    symbol: "BUSD",
    name: "BUSD Token"
  },
  BUSD_ADDRESS
);
var WETH9_ADDRESS = {
  [ChainId_default.BSC]: "0x2170Ed0880ac9A755fd29B2688956BD959F933F8"
};
var WETH9 = addressMapToTokenMap(
  {
    decimals: 18,
    symbol: "WETH",
    name: "Wrapped Ether"
  },
  WETH9_ADDRESS
);
var WNATIVE_ADDRESS = {
  [ChainId_default.BSC]: "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c",
  [ChainId_default.BSC_TESTNET]: "0xae13d989daC2f0dEbFf460aC112a837C89BAa7cd",
  [ChainId_default.SMARTBCH]: "0x3743eC0673453E5009310C727Ba4eaF7b3a1cc04",
  [ChainId_default.SMARTBCH_TESTNET]: "0x17F4FCF5b6E0A95D4eE331c8529041896A073F9b"
};
var WNATIVE = {
  [ChainId_default.BSC]: new Token({
    chainId: ChainId_default.BSC,
    address: WNATIVE_ADDRESS[ChainId_default.BSC],
    decimals: 18,
    symbol: "WBNB",
    name: "Wrapped BNB"
  }),
  [ChainId_default.BSC_TESTNET]: new Token({
    chainId: ChainId_default.BSC_TESTNET,
    address: WNATIVE_ADDRESS[ChainId_default.BSC_TESTNET],
    decimals: 18,
    symbol: "WBNB",
    name: "Wrapped BNB"
  }),
  [ChainId_default.SMARTBCH]: new Token({
    chainId: ChainId_default.SMARTBCH,
    address: WNATIVE_ADDRESS[ChainId_default.SMARTBCH],
    decimals: 18,
    symbol: "WBCH",
    name: "Wrapped BCH"
  }),
  [ChainId_default.SMARTBCH_TESTNET]: new Token({
    chainId: ChainId_default.SMARTBCH_TESTNET,
    address: WNATIVE_ADDRESS[ChainId_default.SMARTBCH_TESTNET],
    decimals: 18,
    symbol: "WBCH",
    name: "Wrapped BCH"
  })
};
var SUSHI_ADDRESS = {
  [ChainId_default.BSC]: "0x947950BcC74888a40Ffa2593C5798F11Fc9124C4"
};
var SUSHI = addressMapToTokenMap(
  {
    decimals: 18,
    symbol: "SUSHI",
    name: "SushiToken"
  },
  SUSHI_ADDRESS
);
var USDC_ADDRESS = {
  [ChainId_default.BSC]: "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d",
  [ChainId_default.SMARTBCH]: "0x7b2B3C5308ab5b2a1d9a94d20D35CCDf61e05b72"
};
var USDC = {
  [ChainId_default.BSC]: new Token({
    chainId: ChainId_default.BSC,
    address: USDC_ADDRESS[ChainId_default.BSC],
    decimals: 6,
    symbol: "USDC",
    name: "USD Coin"
  }),
  [ChainId_default.SMARTBCH]: new Token({
    chainId: ChainId_default.SMARTBCH,
    address: USDC_ADDRESS[ChainId_default.SMARTBCH],
    decimals: 18,
    symbol: "FLEXUSD",
    name: "flexUSD"
  })
};
var USDT_ADDRESS = {
  [ChainId_default.BSC]: "0x55d398326f99059fF775485246999027B3197955",
  [ChainId_default.BSC_TESTNET]: "0xF49E250aEB5abDf660d643583AdFd0be41464EfD"
};
var USDT = __spreadProps(__spreadValues({}, addressMapToTokenMap(
  {
    decimals: 6,
    symbol: "USDT",
    name: "Tether USD"
  },
  USDT_ADDRESS
)), {
  [ChainId_default.BSC]: new Token({
    chainId: ChainId_default.BSC,
    address: USDT_ADDRESS[ChainId_default.BSC],
    decimals: 18,
    symbol: "USDT",
    name: "Tether USD"
  }),
  [ChainId_default.BSC_TESTNET]: new Token({
    chainId: ChainId_default.BSC_TESTNET,
    address: USDT_ADDRESS[ChainId_default.BSC_TESTNET],
    decimals: 18,
    symbol: "USDT",
    name: "Tether USD"
  })
});

// src/constants/routing.ts
var BASES_TO_CHECK_TRADES_AGAINST = {
  [ChainId_default.SMARTBCH]: [WNATIVE[ChainId_default.SMARTBCH], EMBER[ChainId_default.SMARTBCH], USDC[ChainId_default.SMARTBCH]],
  [ChainId_default.SMARTBCH_TESTNET]: [
    WNATIVE[ChainId_default.SMARTBCH_TESTNET],
    EMBER[ChainId_default.SMARTBCH_TESTNET],
    USDC[ChainId_default.SMARTBCH_TESTNET]
  ]
  // [ChainId.BSC]: [WNATIVE[ChainId.BSC]],
};
var ADDITIONAL_BASES = {
  [ChainId_default.SMARTBCH]: {
    [FIRE[ChainId_default.SMARTBCH].address]: [FIRE[ChainId_default.SMARTBCH]]
  }
};
var CUSTOM_BASES = {};
var COMMON_BASES = {
  // [ChainId.SMARTBCH]: [USDC[ChainId.SMARTBCH]],
  // [ChainId.SMARTBCH_TESTNET]: [USDC[ChainId.SMARTBCH_TESTNET]],
  // [ChainId.BSC]: [USDC[ChainId.BSC]],
};

// src/constants/tokenlist.schema.json
var tokenlist_schema_default = {
  $schema: "http://json-schema.org/draft-07/schema#",
  $id: "https://embswap.com/tokenlist.schema.json",
  title: "EmberSwap Token List",
  description: "Schema for lists of tokens compatible with the EmberSwap Interface",
  definitions: {
    Version: {
      type: "object",
      description: "The version of the list, used in change detection",
      examples: [
        {
          major: 1,
          minor: 0,
          patch: 0
        }
      ],
      additionalProperties: false,
      properties: {
        major: {
          type: "integer",
          description: "The major version of the list. Must be incremented when tokens are removed from the list or token addresses are changed.",
          minimum: 0,
          examples: [1, 2]
        },
        minor: {
          type: "integer",
          description: "The minor version of the list. Must be incremented when tokens are added to the list.",
          minimum: 0,
          examples: [0, 1]
        },
        patch: {
          type: "integer",
          description: "The patch version of the list. Must be incremented for any changes to the list.",
          minimum: 0,
          examples: [0, 1]
        }
      },
      required: ["major", "minor", "patch"]
    },
    TagIdentifier: {
      type: "string",
      description: "The unique identifier of a tag",
      minLength: 1,
      maxLength: 10,
      pattern: "^[\\w]+$",
      examples: ["compound", "stablecoin"]
    },
    ExtensionIdentifier: {
      type: "string",
      description: "The name of a token extension property",
      minLength: 1,
      maxLength: 30,
      pattern: "^[\\w]+$",
      examples: ["color", "is_fee_on_transfer", "aliases"]
    },
    ExtensionValue: {
      anyOf: [
        {
          type: "string",
          minLength: 1,
          maxLength: 42,
          examples: ["#00000"]
        },
        {
          type: "boolean",
          examples: [true]
        },
        {
          type: "number",
          examples: [15]
        },
        {
          type: "null"
        }
      ]
    },
    TagDefinition: {
      type: "object",
      description: "Definition of a tag that can be associated with a token via its identifier",
      additionalProperties: false,
      properties: {
        name: {
          type: "string",
          description: "The name of the tag",
          pattern: "^[ \\w]+$",
          minLength: 1,
          maxLength: 20
        },
        description: {
          type: "string",
          description: "A user-friendly description of the tag",
          pattern: "^[ \\w\\.,]+$",
          minLength: 1,
          maxLength: 200
        }
      },
      required: ["name", "description"],
      examples: [
        {
          name: "Stablecoin",
          description: "A token with value pegged to another asset"
        }
      ]
    },
    TokenInfo: {
      type: "object",
      description: "Metadata for a single token in a token list",
      additionalProperties: false,
      properties: {
        chainId: {
          type: "integer",
          description: "The chain ID of the EVM network where this token is deployed",
          minimum: 1,
          examples: [1, 42]
        },
        address: {
          type: "string",
          description: "The checksummed address of the token on the specified chain ID",
          pattern: "^0x[a-fA-F0-9]{40}$",
          examples: ["0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48"]
        },
        decimals: {
          type: "integer",
          description: "The number of decimals for the token balance",
          minimum: 0,
          maximum: 255,
          examples: [18]
        },
        name: {
          type: "string",
          description: "The name of the token",
          minLength: 1,
          maxLength: 40,
          pattern: "^[ \\w.'+\\-%/\xC0-\xD6\xD8-\xF6\xF8-\xFF\\:]+$",
          examples: ["USD Coin"]
        },
        symbol: {
          type: "string",
          description: "The symbol for the token; must be alphanumeric",
          pattern: "^[a-zA-Z0-9+\\-%/\\$]+$",
          minLength: 1,
          maxLength: 20,
          examples: ["USDC"]
        },
        logoURI: {
          type: "string",
          description: "A URI to the token logo asset; if not set, interface will attempt to find a logo based on the token address; suggest SVG or PNG of size 64x64",
          format: "uri",
          examples: ["ipfs://QmXfzKRvjZz3u5JRgC4v5mGVbm9ahrUiB4DgzHBsnWbTMM"]
        },
        tags: {
          type: "array",
          description: "An array of tag identifiers associated with the token; tags are defined at the list level",
          items: {
            $ref: "#/definitions/TagIdentifier"
          },
          maxLength: 10,
          examples: ["stablecoin", "compound"]
        },
        extensions: {
          type: "object",
          description: "An object containing any arbitrary or vendor-specific token metadata",
          propertyNames: {
            $ref: "#/definitions/ExtensionIdentifier"
          },
          additionalProperties: {
            $ref: "#/definitions/ExtensionValue"
          },
          maxProperties: 10,
          examples: [
            {
              color: "#000000",
              is_verified_by_me: true
            }
          ]
        }
      },
      required: ["chainId", "address", "decimals", "name", "symbol"]
    }
  },
  type: "object",
  additionalProperties: false,
  properties: {
    name: {
      type: "string",
      description: "The name of the token list",
      minLength: 1,
      maxLength: 20,
      pattern: "^[\\w ]+$",
      examples: ["My Token List"]
    },
    timestamp: {
      type: "string",
      format: "date-time",
      description: "The timestamp of this list version; i.e. when this immutable version of the list was created"
    },
    version: {
      $ref: "#/definitions/Version"
    },
    tokens: {
      type: "array",
      description: "The list of tokens included in the list",
      items: {
        $ref: "#/definitions/TokenInfo"
      },
      minItems: 1,
      maxItems: 1e4
    },
    keywords: {
      type: "array",
      description: "Keywords associated with the contents of the list; may be used in list discoverability",
      items: {
        type: "string",
        description: "A keyword to describe the contents of the list",
        minLength: 1,
        maxLength: 20,
        pattern: "^[\\w ]+$",
        examples: ["compound", "lending", "personal tokens", "tax"]
      },
      maxItems: 20,
      uniqueItems: true
    },
    tags: {
      type: "object",
      description: "A mapping of tag identifiers to their name and description",
      propertyNames: {
        $ref: "#/definitions/TagIdentifier"
      },
      additionalProperties: {
        $ref: "#/definitions/TagDefinition"
      },
      maxProperties: 20,
      examples: [
        {
          stablecoin: {
            name: "Stablecoin",
            description: "A token with value pegged to another asset"
          }
        }
      ]
    },
    logoURI: {
      type: "string",
      description: "A URI for the logo of the token list; prefer SVG or PNG of size 256x256",
      format: "uri",
      examples: ["ipfs://QmXfzKRvjZz3u5JRgC4v5mGVbm9ahrUiB4DgzHBsnWbTMM"]
    }
  },
  required: ["name", "timestamp", "version", "tokens"]
};
export {
  ADDITIONAL_BASES,
  BASES_TO_CHECK_TRADES_AGAINST,
  BLOCK_DELAY,
  BUSD,
  BUSD_ADDRESS,
  CHAINS,
  COMMON_BASES,
  CUSTOM_BASES,
  DISTRIBUTOR_ADDRESS,
  EMBER,
  EMBER_ADDRESS,
  FACTORY_ADDRESS,
  FIRE,
  FIRE_ADDRESS,
  FIVE,
  INIT_CODE_HASH,
  LOCKER_ADDRESS,
  MAX_SAFE_INTEGER,
  MAX_UINT128,
  MAX_UINT256,
  MULTICALL2_ADDRESS,
  ONE,
  ROUTER_ADDRESS,
  ROUTER_PROCESSOR_ADDRESS,
  SUPPORTED_CHAIN_IDS,
  SUSHI,
  SUSHI_ADDRESS,
  TEN,
  THREE,
  TWO,
  USDC,
  USDC_ADDRESS,
  USDT,
  USDT_ADDRESS,
  VAULT_ADDRESS,
  WETH9,
  WETH9_ADDRESS,
  WNATIVE,
  WNATIVE_ADDRESS,
  ZERO,
  ZERO_PERCENT,
  _100,
  _1000,
  _10000,
  _1e12,
  _1e18,
  _1e6,
  _1e9,
  _997,
  _9994,
  _9995,
  chainIds,
  chainName,
  chainShortName,
  chainShortNameToChainId,
  chains,
  tokenlist_schema_default as scheme
};
//# sourceMappingURL=index.mjs.map